import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiServices from '../../services/apiServices';

// Thunks
export const fetchExpenses = createAsyncThunk(
    'expenses/fetchExpenses',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiServices.get('/expenses', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch expenses');
        }
    }
);

export const createExpense = createAsyncThunk(
    'expenses/createExpense',
    async (expenseData, { rejectWithValue }) => {
        try {
            const response = await apiServices.post('/expenses', expenseData);
            return response.data.expense;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create expense');
        }
    }
);

export const updateExpense = createAsyncThunk(
    'expenses/updateExpense',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await apiServices.put(`/expenses/${id}`, data);
            return response.data.expense;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update expense');
        }
    }
);

export const deleteExpense = createAsyncThunk(
    'expenses/deleteExpense',
    async (id, { rejectWithValue }) => {
        try {
            await apiServices.delete(`/expenses/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete expense');
        }
    }
);

export const fetchCategorySummary = createAsyncThunk(
    'expenses/fetchCategorySummary',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiServices.get('/expenses/category-summary', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch summary');
        }
    }
);

export const fetchProfitLoss = createAsyncThunk(
    'expenses/fetchProfitLoss',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiServices.get('/expenses/reports/profit-loss', { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch P&L');
        }
    }
);

// Slice
const expensesSlice = createSlice({
    name: 'expenses',
    initialState: {
        expenses: [],
        categorySummary: null,
        profitLoss: null,
        loading: false,
        error: null,
        totalPages: 1,
        currentPage: 1,
        total: 0
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch expenses
            .addCase(fetchExpenses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchExpenses.fulfilled, (state, action) => {
                state.loading = false;
                state.expenses = action.payload.expenses;
                state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.currentPage;
                state.total = action.payload.total;
            })
            .addCase(fetchExpenses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Create expense
            .addCase(createExpense.fulfilled, (state, action) => {
                state.expenses.unshift(action.payload);
                state.total += 1;
            })
            .addCase(createExpense.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Update expense
            .addCase(updateExpense.fulfilled, (state, action) => {
                const index = state.expenses.findIndex(e => e._id === action.payload._id);
                if (index !== -1) {
                    state.expenses[index] = action.payload;
                }
            })
            .addCase(updateExpense.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Delete expense
            .addCase(deleteExpense.fulfilled, (state, action) => {
                state.expenses = state.expenses.filter(e => e._id !== action.payload);
                state.total -= 1;
            })
            .addCase(deleteExpense.rejected, (state, action) => {
                state.error = action.payload;
            })
            // Fetch category summary
            .addCase(fetchCategorySummary.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCategorySummary.fulfilled, (state, action) => {
                state.loading = false;
                state.categorySummary = action.payload;
            })
            .addCase(fetchCategorySummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fetch P&L
            .addCase(fetchProfitLoss.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchProfitLoss.fulfilled, (state, action) => {
                state.loading = false;
                state.profitLoss = action.payload;
            })
            .addCase(fetchProfitLoss.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { clearError } = expensesSlice.actions;
export default expensesSlice.reducer;
